import { readCustomBackgroundImage, readCustomBackgroundThumbnail } from './customBackgroundStorage';

/**
 * Memoised accessors for the stored data URLs.
 *
 * **This cache is about referential stability, not speed.** `localStorage.getItem` allocates a fresh
 * ~165K-character string on every call. That string ends up in the dependency array of the effect
 * that applies the background to the published track, so an unmemoised read would look like a new
 * value on every render and re-apply the Agora processor in a loop. Returning the same string
 * instance is what makes the effect settle.
 *
 * Mirrors the `loadBackgroundImage` cache one layer up, which memoises URL -> HTMLImageElement for
 * the same reason.
 */
const cache = new Map<string, string>();

const memoised = (key: string, read: () => string | undefined) => {
  const cached = cache.get(key);
  if (cached) return cached;

  const value = read();
  if (value) cache.set(key, value);

  return value;
};

export const getCustomBackgroundImageUrl = (id: string) => memoised(`image:${id}`, () => readCustomBackgroundImage(id));

export const getCustomBackgroundThumbnailUrl = (id: string) =>
  memoised(`thumb:${id}`, () => readCustomBackgroundThumbnail(id));

/** Call after removing a background, so a later id reuse can't read the old bytes. */
export const invalidateCustomBackgroundUrls = (id: string) => {
  cache.delete(`image:${id}`);
  cache.delete(`thumb:${id}`);
};

/** Call when another tab has rewritten storage underneath us, and as a test seam. */
export const invalidateAllCustomBackgroundUrls = () => cache.clear();
